import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Users, Edit, Trash2, Shield, Loader2, UserPlus, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface User {
  id: string;
  email: string;
  full_name: string | null;
  org_id: string | null;
  created_at: string;
  roles?: { role: string }[];
}

interface OrgUsersManagerProps {
  orgId: string;
  orgName: string;
}

export function OrgUsersManager({ orgId, orgName }: OrgUsersManagerProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    role: "owner" as "owner" | "admin" | "manager" | "rep" | "client"
  });

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open, orgId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Load roles for each user
      const usersWithRoles = await Promise.all(
        (profiles || []).map(async (profile) => {
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', profile.id)
            .eq('org_id', orgId);
          
          return {
            ...profile,
            roles: roles || []
          };
        })
      );

      setUsers(usersWithRoles);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name || "",
      email: user.email,
      role: (user.roles?.[0]?.role as any) || "owner"
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          full_name: formData.full_name,
          email: formData.email 
        })
        .eq('id', editingUser.id);

      if (profileError) throw profileError;

      // Update role if changed
      const currentRole = editingUser.roles?.[0]?.role as "owner" | "admin" | "manager" | "rep" | "client" | undefined;
      if (currentRole !== formData.role) {
        // Delete old role
        if (currentRole) {
          await supabase
            .from('user_roles')
            .delete()
            .eq('user_id', editingUser.id)
            .eq('org_id', orgId)
            .eq('role', currentRole);
        }

        // Insert new role
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: editingUser.id,
            org_id: orgId,
            role: formData.role as any
          });

        if (roleError) throw roleError;
      }

      toast.success("User updated successfully");
      setEditingUser(null);
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update user:', error);
      toast.error("Failed to update user");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm("Are you sure you want to remove this user from the organization?")) {
      return;
    }

    try {
      // Remove user's org association
      const { error } = await supabase
        .from('profiles')
        .update({ org_id: null })
        .eq('id', userId);

      if (error) throw error;

      // Delete user's roles for this org
      await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('org_id', orgId);

      toast.success("User removed from organization");
      loadUsers();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast.error("Failed to remove user");
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    try {
      setInviting(true);

      // Invite user via edge function
      const { data, error } = await supabase.functions.invoke('platform-admin-invite-user', {
        body: {
          email: inviteEmail,
          org_id: orgId,
          full_name: inviteEmail.split('@')[0]
        }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      loadUsers();
    } catch (error: any) {
      console.error('Failed to invite user:', error);
      toast.error(`Failed to send invitation: ${error.message}`);
    } finally {
      setInviting(false);
    }
  };

  const handleResetPassword = async (userEmail: string) => {
    if (!confirm(`Send password reset email to ${userEmail}?`)) return;

    try {
      const { data, error } = await supabase.functions.invoke('admin-reset-password', {
        body: { userEmail }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Password reset email sent to ${userEmail}`);
    } catch (error: any) {
      console.error('Failed to reset password:', error);
      toast.error(`Failed to send reset email: ${error.message}`);
    }
  };

  const handleResendInvite = async (userEmail: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('platform-admin-invite-user', {
        body: {
          email: userEmail,
          org_id: orgId,
          full_name: userEmail.split('@')[0]
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Invitation resent to ${userEmail}`);
    } catch (error: any) {
      console.error('Failed to resend invitation:', error);
      toast.error(`Failed to resend invitation: ${error.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Users className="w-4 h-4" />
          Manage Users
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Users in {orgName}</DialogTitle>
          <DialogDescription>
            Manage user accounts and roles for this organization
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Invite New User */}
            <div className="border rounded-lg p-4 bg-muted/50">
              <h3 className="font-semibold flex items-center gap-2 mb-3">
                <UserPlus className="w-4 h-4" />
                Invite New User
              </h3>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="user@email.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  disabled={inviting}
                />
                <Button onClick={handleInviteUser} disabled={inviting || !inviteEmail}>
                  {inviting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Send Invite
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                An invitation email will be sent to join this organization
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Security actions for this email:</span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => inviteEmail && handleResetPassword(inviteEmail)}
                  disabled={!inviteEmail}
                >
                  Reset Password
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => inviteEmail && handleResendInvite(inviteEmail)}
                  disabled={!inviteEmail}
                >
                  Resend Invite
                </Button>
              </div>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.full_name || 'N/A'}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {user.roles && user.roles.length > 0 ? (
                              user.roles.map((r, i) => (
                                <Badge key={i} variant="secondary" className="gap-1">
                                  <Shield className="w-3 h-3" />
                                  {r.role}
                                </Badge>
                              ))
                            ) : (
                              <Badge variant="outline">No role</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {format(new Date(user.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditUser(user)}
                              title="Edit user details"
                              className="gap-1"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="hidden sm:inline">Edit</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResetPassword(user.email)}
                              title="Send password reset email"
                              className="gap-1 border-orange-500 text-orange-600 hover:bg-orange-50"
                            >
                              <KeyRound className="w-4 h-4" />
                              <span>Reset Password</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResendInvite(user.email)}
                              title="Resend invitation email"
                              className="gap-1 border-blue-500 text-blue-600 hover:bg-blue-50"
                            >
                              <Mail className="w-4 h-4" />
                              <span>Resend Invite</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(user.id)}
                              title="Remove user from organization"
                              className="gap-1 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span className="hidden sm:inline">Remove</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Edit User Form */}
            {editingUser && (
              <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
                <h3 className="font-semibold flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Edit User: {editingUser.email}
                </h3>
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">Full Name</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value: any) => setFormData({ ...formData, role: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="rep">Rep</SelectItem>
                        <SelectItem value="client">Client</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSaveUser}>Save Changes</Button>
                    <Button variant="outline" onClick={() => setEditingUser(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
